import React, { useEffect, useMemo, useState } from 'react';
import ArticleIcon from '@mui/icons-material/Article';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { showToast } from '@/features/toast/toastSlice';
import {
	useGenerateEngageReportMutation,
	useGetEngageReportsQuery,
} from '@/features/deals/dealsApi';
import { useGetClientRowQuery } from '@/features/clients/clientsApi';
import useGetAIMessages from '@/hooks/useGetAIMessages';
import { useObjectContext } from '@/hooks/useObjectContext';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import GenerateMenuItemRow from '@/components/buttons/GenerateAITemplateMenuButton/GenerateMenuItemRow';
import { getMissingDependenciesMessage } from '@/components/buttons/GenerateAITemplateMenuButton/helpers';
import { ENGAGE_REPORT_STATUSES, ENGAGE_REPORT_TYPES } from '@/utils/engageReports';

const FAILED_STATUSES = [ENGAGE_REPORT_STATUSES.failed, ENGAGE_REPORT_STATUSES.skipped];

const EngageReportMenuItem = ({ reportType, onCloseMenu }) => {
	const dispatch = useDispatch();
	const { dealId } = useParams();

	const [confirmationOpen, setConfirmationOpen] = useState(false);

	const { title, name, summaryType } = ENGAGE_REPORT_TYPES[reportType];

	const { getLatestSummaries, isLoading, isUninitialized, isError, isSuccess } = useGetAIMessages(
		{ dealId },
	);

	// The summary the user sees in the tab (saving an edit creates a new message)
	const summary = useMemo(
		() => getLatestSummaries([summaryType])[summaryType],
		[isLoading, isUninitialized, isError, isSuccess, summaryType, getLatestSummaries],
	);

	const { latestReport, isReportsLoading } = useGetEngageReportsQuery(dealId, {
		skip: !dealId,
		// Menu items are only mounted while the menu is open, so this polls only then
		refetchOnMountOrArgChange: true,
		pollingInterval: 20000,
		selectFromResult: ({ data, isLoading }) => ({
			latestReport: data?.find((r) => r.reportType === reportType),
			isReportsLoading: isLoading,
		}),
	});
	const isRunning = Boolean(latestReport?.isRunning);

	// The share link is sent to the client's contact email, so a report can't go out without one
	const { dealRow } = useObjectContext();
	const { hasContactEmail, isClientLoading } = useGetClientRowQuery(dealRow?.clientDocId, {
		skip: !dealRow?.clientDocId,
		selectFromResult: ({ data, isLoading }) => ({
			hasContactEmail: Boolean(data?.contactEmail),
			isClientLoading: isLoading,
		}),
	});

	const [generateEngageReport, { isLoading: isQueueing }] = useGenerateEngageReportMutation();

	const disabled =
		isLoading ||
		isReportsLoading ||
		isClientLoading ||
		isRunning ||
		isQueueing ||
		!summary ||
		!hasContactEmail;

	const disabledMessage = isLoading
		? 'Loading messages ...'
		: isReportsLoading
			? 'Loading reports ...'
			: isClientLoading
				? 'Loading client ...'
				: isRunning || isQueueing
					? `Generating ${name} ...`
					: !summary
						? getMissingDependenciesMessage([summaryType])
						: !hasContactEmail
							? 'The client has no contact email. Add one to the client to generate this report.'
							: '';

	const lastFailure =
		!disabledMessage && FAILED_STATUSES.includes(latestReport?.status)
			? `Last run failed: ${latestReport.errorMessage}`
			: '';

	const handleGenerate = async () => {
		setConfirmationOpen(false);
		const result = await generateEngageReport({ dealId, reportType, aiMessageId: summary.id });
		if (result.error) {
			dispatch(
				showToast({
					message:
						result.error.data?.error ?? `Couldn't start the ${name}; please try again.`,
					error: true,
				}),
			);
		} else {
			dispatch(
				showToast({
					message: `Generating ${name}. You'll get a notification when it's ready.`,
					success: true,
				}),
			);
		}
		onCloseMenu?.();
	};

	const dialogContent = `This creates a new Folio Engage report and share link from the latest ${summaryType === 'quoteSummary' ? 'Quote ' : summaryType === 'riskInfoSummary' ? 'Risk Info ' : ''}summary on this deal.`;
	return (
		<>
			<GenerateMenuItemRow
				title={title}
				icon={<ArticleIcon fontSize='small' />}
				message={disabledMessage || lastFailure}
				isBusy={isRunning || isQueueing}
				showWarning={!summary || !hasContactEmail || Boolean(lastFailure)}
				disabled={disabled}
				onClick={() => setConfirmationOpen(true)}
			/>

			<ConfirmDialog
				openDialog={confirmationOpen}
				handleClose={() => setConfirmationOpen(false)}
				handleConfirm={handleGenerate}
				title={title}
				content={dialogContent}
			/>
		</>
	);
};
EngageReportMenuItem.displayName = 'EngageReportMenuItem';
export default EngageReportMenuItem;
